import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AiProviderConfig } from '@mailpilot/shared';
import clsx from 'clsx';
import { Bot, CheckCircle2, Cpu, KeyRound, Pencil, Plug, Plus, Puzzle, Sparkles, Trash2, XCircle } from 'lucide-react';
import { api, type ResOf } from '../../lib/api';
import { qk } from '../../lib/keys';
import { fullDate, relativeTime } from '../../lib/format';
import { Badge, Button, Card, CardBody, CardDivider, CardHeader, EmptyState, ErrorState, Segmented, Skeleton, useConfirm, useToast } from '../../components/ui';
import { useSettings, useUpdateSettings } from '../../hooks/useMe';
import { useCurrentWorkspace } from '../../hooks/useWorkspace';
import { AiConsentCard, AiPrivacyCard } from '../../components/settings/AiConsentPrivacy';
import { AiProviderEditor, showsEndpoint, type KindInfo } from '../../components/settings/AiProviderEditor';
import { AiTasksCard, AiUsageCard } from '../../components/settings/AiTasksUsage';
import { TipIconButton } from '../../components/settings/SettingsPrimitives';
import { SettingsTitle } from './SettingsLayout';

type TestResult = ResOf<'POST /api/ai/providers/:id/test'>;

export function AiSettings() {
  const q = useQuery({ queryKey: qk.aiProviders, queryFn: () => api('GET /api/ai/providers') });
  const [editing, setEditing] = useState<AiProviderConfig | 'new' | null>(null);
  const providers = q.data?.providers ?? [];
  const kinds = q.data?.kinds ?? [];
  const tasks = q.data?.tasks ?? [];
  const builtin = q.data?.builtin ?? null;
  const qc = useQueryClient();
  const settings = useSettings();
  const updateSettings = useUpdateSettings();
  const { isAdmin } = useCurrentWorkspace();
  const engineLabel = builtin?.engines.find((e) => e.kind === builtin.kind)?.label;
  const draftAssignment = tasks.find((t) => t.task === 'draft');
  const active = providers.find((p) => p.id === draftAssignment?.providerId) ?? providers[0] ?? null;
  const activeModel = active ? (draftAssignment?.providerId === active.id && draftAssignment.model ? draftAssignment.model : active.defaultModel) : null;

  return (
    <div>
      <SettingsTitle
        title="AI providers & privacy"
        description="Bring your own AI provider and key. MailPilot isn't locked to one AI company, and nothing is sent until you consent."
      />
      <div className="space-y-6">
        <AiConsentCard />

        {q.isLoading ? (
          <Card aria-busy="true">
            <CardBody className="space-y-4">
              <div className="flex items-center gap-3.5">
                <Skeleton className="h-10 w-10 rounded-[13px]" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-64 max-w-full" />
                </div>
              </div>
              <Skeleton className="h-16 w-full rounded-2xl" />
              <Skeleton className="h-16 w-full rounded-2xl" />
            </CardBody>
          </Card>
        ) : q.isError ? (
          <Card>
            <ErrorState error={q.error} onRetry={() => void q.refetch()} title="Couldn't load AI providers" />
          </Card>
        ) : (
          <>
            <Card>
              <CardHeader
                icon={<Bot className="h-5 w-5" aria-hidden />}
                title="Providers"
                description={
                  active ? (
                    <>
                      Drafting with <strong className="font-medium text-ink">{active.label}</strong> · <span className="font-mono text-xs">{activeModel}</span>
                    </>
                  ) : builtin ? (
                    <>
                      Drafting with <strong className="font-medium text-ink">{builtin.label}</strong> · <span className="font-mono text-xs">{builtin.model}</span>
                    </>
                  ) : (
                    'AI features stay off until you add one.'
                  )
                }
                actions={
                  providers.length > 0 && (
                    <Button variant="primary" size="sm" icon={<Plus className="h-4 w-4" aria-hidden />} onClick={() => setEditing('new')} disabled={kinds.length === 0}>
                      Add provider
                    </Button>
                  )
                }
              />
              {providers.length === 0 && builtin ? (
                <>
                  <CardDivider className="mt-5" />
                  <div className="flex flex-wrap items-center gap-4 px-6 py-5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px] bg-primary-soft text-primary-ink">
                      <Sparkles className="h-5 w-5" aria-hidden />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-2 text-[0.875rem] font-medium text-ink">
                        {builtin.label} <Badge tone="success">Included</Badge>
                      </p>
                      <p className="mt-0.5 text-xs text-muted">
                        Built into MailPilot, no API key needed{engineLabel ? ` — currently ${engineLabel}` : ''}. {builtin.monthlyLimitUsd === null ? 'No monthly limit.' : `$${builtin.spentThisMonthUsd.toFixed(2)} of $${builtin.monthlyLimitUsd.toFixed(2)} used this month.`} Add your own provider to use it instead.
                      </p>
                    </div>
                    <Button variant="secondary" size="sm" icon={<Plus className="h-4 w-4" aria-hidden />} onClick={() => setEditing('new')} disabled={kinds.length === 0}>
                      Use your own provider
                    </Button>
                  </div>
                  {builtin.engines.length > 1 && (
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-6 pb-5">
                      <div className="min-w-0 flex-1">
                        <p className="text-[0.8125rem] font-medium text-ink">AI engine</p>
                        <p className="mt-0.5 text-xs text-muted">
                          {isAdmin ? 'Pick which model answers for this workspace. ' : "Only your organization's admins can change this. "}
                          <span className="font-mono">{builtin.model}</span>
                          {builtin.kind === builtin.defaultKind && ' · default'}
                        </p>
                      </div>
                      <Segmented
                        size="sm"
                        label="Built-in AI engine"
                        value={builtin.kind}
                        options={builtin.engines.map((e) => ({ value: e.kind, label: e.label, disabled: !isAdmin || updateSettings.isPending }))}
                        onChange={(kind) => {
                          if (!settings || kind === builtin.kind) return;
                          updateSettings.mutate({ ai: { ...settings.ai, builtinEngine: kind } }, { onSuccess: () => void qc.invalidateQueries({ queryKey: qk.aiProviders }) });
                        }}
                      />
                    </div>
                  )}
                </>
              ) : providers.length === 0 ? (
                <EmptyState
                  icon={<Bot className="h-6 w-6" aria-hidden />}
                  title="No AI provider connected"
                  description="Add OpenAI, Anthropic, Gemini, OpenRouter, any OpenAI-compatible endpoint, or a local model through the desktop agent."
                  actions={
                    <Button variant="primary" icon={<Plus className="h-4 w-4" aria-hidden />} onClick={() => setEditing('new')} disabled={kinds.length === 0}>
                      Add your first provider
                    </Button>
                  }
                />
              ) : (
                <>
                  <CardDivider className="mt-5" />
                  <ul className="divide-y divide-line/70">
                    {providers.map((p) => (
                      <ProviderRow key={p.id} p={p} kinds={kinds} providers={providers} isActive={p.id === active?.id} onEdit={() => setEditing(p)} />
                    ))}
                  </ul>
                </>
              )}
              <CardDivider />
              <p className="flex items-start gap-2 px-6 py-4 text-xs leading-relaxed text-muted">
                <Puzzle className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
                <span>
                  OpenAI, Anthropic, Gemini, OpenRouter and OpenAI-compatible endpoints work out of the box; local models connect through the desktop agent. A different API format needs a small server adapter — see <span className="font-mono">docs/AI_ADAPTERS.md</span>.
                </span>
              </p>
            </Card>

            <AiTasksCard tasks={tasks} providers={providers} kinds={kinds} />
            <AiUsageCard providers={providers} builtinLabel={builtin?.label} />
          </>
        )}

        <AiPrivacyCard />
      </div>

      {editing && (
        <AiProviderEditor
          key={editing === 'new' ? 'new' : editing.id}
          open
          onClose={() => setEditing(null)}
          provider={editing === 'new' ? null : editing}
          kinds={kinds}
          providers={providers}
        />
      )}
    </div>
  );
}

function ProviderRow({ p, kinds, providers, isActive, onEdit }: { p: AiProviderConfig; kinds: KindInfo[]; providers: AiProviderConfig[]; isActive: boolean; onEdit: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const confirm = useConfirm();
  const [result, setResult] = useState<TestResult | null>(null);
  const kind = kinds.find((k) => k.kind === p.kind);
  const fallback = p.fallbackProviderId ? providers.find((x) => x.id === p.fallbackProviderId) : null;

  const test = useMutation({
    mutationFn: () => api('POST /api/ai/providers/:id/test', { params: { id: p.id } }),
    onSuccess: (res) => {
      setResult(res);
      void qc.invalidateQueries({ queryKey: qk.aiProviders });
    },
    onError: (e) => toast.error(e),
  });

  const del = useMutation({
    mutationFn: () => api('DELETE /api/ai/providers/:id', { params: { id: p.id } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.aiProviders });
      toast.success(`${p.label} removed`);
    },
    onError: (e) => toast.error(e),
  });

  const askDelete = async () => {
    const ok = await confirm({
      title: `Remove ${p.label}?`,
      message: 'Its encrypted API key is deleted. Tasks assigned to it become unassigned until you choose another provider.',
      confirmLabel: 'Remove provider',
      danger: true,
    });
    if (ok) del.mutate();
  };

  const budgetPct = p.monthlyBudgetUsd ? Math.min(100, Math.round((p.spentThisMonthUsd / p.monthlyBudgetUsd) * 100)) : null;
  const shownTest: { ok: boolean; message: string; latency?: number; at?: string | null } | null = result
    ? { ok: result.ok, message: result.message, latency: result.latencyMs }
    : p.lastTestAt
      ? { ok: !!p.lastTestOk, message: p.lastTestMessage ?? '', at: p.lastTestAt }
      : null;

  const keyText = p.hasApiKey ? `Key ${p.apiKeyHint ?? '••••'}` : kind?.needsKey ? 'No key saved' : 'No key needed';

  return (
    <li className="group px-6 py-5 transition-colors hover:bg-raised/40">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
        <div className="flex min-w-0 items-start gap-3.5">
          <span className={clsx('mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-[13px]', isActive ? 'bg-primary-soft text-primary-ink' : 'bg-sunken text-muted')}>
            {p.kind === 'local_agent' ? <Cpu className="h-5 w-5" aria-hidden /> : <Bot className="h-5 w-5" aria-hidden />}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-semibold text-ink">{p.label}</h3>
              <Badge>{kind?.label ?? p.kind}</Badge>
              {isActive && <Badge tone="primary">Active for drafts</Badge>}
            </div>
            <p className="mt-0.5 font-mono text-xs text-muted">{p.defaultModel}</p>
          </div>
        </div>
        <div className="flex items-center gap-1 sm:ml-auto">
          <Button size="sm" icon={<Plug className="h-4 w-4" aria-hidden />} loading={test.isPending} onClick={() => test.mutate()}>
            Test connection
          </Button>
          <div className="flex items-center transition-opacity [@media(hover:hover)]:opacity-50 group-hover:opacity-100 group-focus-within:opacity-100">
            <TipIconButton size="sm" label={`Edit ${p.label}`} icon={<Pencil className="h-4 w-4" aria-hidden />} onClick={onEdit} />
            <TipIconButton size="sm" label={`Remove ${p.label}`} tipPlacement="bottom-end" icon={<Trash2 className="h-4 w-4" aria-hidden />} disabled={del.isPending} onClick={() => void askDelete()} className="hover:!bg-danger-soft hover:!text-danger-ink" />
          </div>
        </div>
      </div>

      <dl className="mt-4 grid gap-x-6 gap-y-3 text-[0.8125rem] sm:grid-cols-2 lg:grid-cols-4">
        {(showsEndpoint(kind) || p.endpoint) && (
          <div className="min-w-0 sm:col-span-2 lg:col-span-4">
            <dt className="text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">Endpoint</dt>
            <dd className="mt-0.5 truncate font-mono text-xs text-ink" title={p.endpoint ?? undefined}>
              {p.endpoint ?? 'Default'}
            </dd>
          </div>
        )}
        <div>
          <dt className="text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">API key</dt>
          <dd className={clsx('mt-0.5 flex items-center gap-1.5', !p.hasApiKey && kind?.needsKey ? 'text-danger-ink' : 'text-ink')}>
            <KeyRound className="h-3.5 w-3.5 text-muted" aria-hidden />
            {keyText}
          </dd>
        </div>
        <div>
          <dt className="text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">Timeout</dt>
          <dd className="mt-0.5 text-ink">{Math.round(p.timeoutMs / 1000)} seconds</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">Spend this month</dt>
          <dd className="mt-0.5 text-ink">
            ${p.spentThisMonthUsd.toFixed(2)}
            {p.monthlyBudgetUsd !== null ? <span className="text-muted"> of ${p.monthlyBudgetUsd.toFixed(2)}</span> : <span className="text-muted"> · no limit</span>}
            {budgetPct !== null && (
              <span className="mt-1.5 block h-1.5 w-full max-w-[10rem] overflow-hidden rounded-full bg-sunken" aria-hidden>
                <span className={clsx('block h-full rounded-full', budgetPct >= 90 ? 'bg-danger' : 'bg-primary')} style={{ width: `${budgetPct}%` }} />
              </span>
            )}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">Fallback</dt>
          <dd className="mt-0.5 text-ink">
            {fallback ? (
              <>
                {fallback.label}
                {p.fallbackModel ? <span className="font-mono text-xs text-muted"> · {p.fallbackModel}</span> : null}
                <span className="block text-xs text-muted">{p.allowFallback ? 'Switches automatically' : 'Stops and tells you instead'}</span>
              </>
            ) : (
              <>
                None
                <span className="block text-xs text-muted">Stops safely if this provider fails</span>
              </>
            )}
          </dd>
        </div>
      </dl>

      {shownTest && (
        <div className={clsx('mt-4 flex items-start gap-2 rounded-xl px-3 py-2 text-[0.8125rem]', shownTest.ok ? 'bg-success-soft text-success-ink' : 'bg-danger-soft text-danger-ink')} role="status">
          {shownTest.ok ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />}
          <span className="min-w-0">
            <strong>{shownTest.ok ? 'Connection works' : 'Connection failed'}</strong>
            {shownTest.message && ` — ${shownTest.message}`}
            {shownTest.latency !== undefined && ` (${shownTest.latency} ms)`}
            {shownTest.at && (
              <span className="opacity-80" title={fullDate(shownTest.at)}>
                {' '}
                · tested {relativeTime(shownTest.at)}
              </span>
            )}
          </span>
        </div>
      )}
    </li>
  );
}
